<template>
  <v-container fluid>
    <v-layout row>
      <v-flex xs12 md10 offset-md1 lg8 offset-lg2>
        <v-breadcrumbs divider="/">
          <v-breadcrumbs-item :to="{ name: 'my-services' }" exact>Mine tjenester</v-breadcrumbs-item>
          <v-breadcrumbs-item :to="{ name: 'email-list' }" exact>E-postkontoer</v-breadcrumbs-item>
          <v-breadcrumbs-item disabled>
            <span v-if="isNew && type === 'mailbox'">Opprett e-postkonto</span>
            <span v-else-if="isNew && type === 'forward'">Opprett videresending</span>
            <span v-else>{{ originalEmail }}</span>
            {{ state.servername }}
          </v-breadcrumbs-item>
          <v-spacer />
          <v-btn
            color="red"
            dark
            @click.stop="dialog = true"
            v-if="!isNew"
          >
            Slett
          </v-btn>
          <v-dialog
            v-model="dialog"
            max-width="290"
          >
            <v-card>
              <v-card-title class="headline">Slett e-postadresse?</v-card-title>
              <v-card-text>
                Hvis du klikker "Ja" vil e-postadressen slettes. All e-post som kontoen har fått vil bli slettet fra mailserveren, og ny e-post som blir sendt til adressen vil ikke komme fram, med mindre adressen blir opprettet igjen, enten som en konto, eller som et alias til en annen konto.
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>

                <v-btn
                  color="green darken-1"
                  flat="flat"
                  @click="dialog = false"
                >
                  Nei
                </v-btn>

                <v-btn
                  color="red"
                  flat="flat"
                  @click="deleteMailbox"
                >
                  Ja
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-breadcrumbs>

        <v-progress-linear v-if="isSubmitting" :indeterminate="true"></v-progress-linear>
        <notifications group="email-detail" key="email-detail" />

        <v-form>
          <v-card class="pa-3" raised>
            <v-card-text>
              <h3>E-postadresse</h3>
              <v-combobox
                solo
                :items="emailSuggestions"
                label="E-postadresse"
                v-model="state.mail"
                class="required"
                :error-messages=emailErrors
                ref="focusField"
                @keyup="emailSuggestion($event)"
                @click="emailSuggestion($event)"
              ></v-combobox>

              <h4>Aliaser (en ekstra e-postadresse til samme konto)</h4>
              <v-combobox
                solo
                :items="emailSuggestions"
                hide-details
                append-icon="close"
                label="E-postadresse"
                @click:append="state.aliases.splice(i, 1)"
                v-for="(alias, i) in state.aliases"
                v-model="state.aliases[i]"
                :key="state.mail + '-alias-' + i"
                @keyup="emailSuggestion($event)"
                @click="emailSuggestion($event)"
              ></v-combobox>
            </v-card-text>
            <v-card-actions>
              <v-btn flat color="primary" @click="state.aliases.push('')">Flere alias</v-btn>
            </v-card-actions>
          </v-card>

          <v-card class="pa-3 mt-3" raised>
            <h3>Videresendes til</h3>

            <v-card-text>
              <p v-if="type !== 'forward'">
                Selv om dette er en mailboks, har du muligheten til å videresende innkommende epost. Hvis du
                benytter deg av dette, så vil e-post fortsatt havne i innboksen din. Videresendingen skjer i tillegg.
              </p>
              <p v-if="type !== 'forward'">
                Vi anbefaler ikke å bruke dette utenom vår mailserver da det er mange eksterne mailservere som ikke vil godta slike videresendinger.
              </p>

              <v-text-field
                class="required"
                append-icon="close"
                label="E-postadresse"
                @click:append="state.forwards.splice(i, 1)"
                v-for="(forwards, i) in state.forwards"
                v-model="state.forwards[i]"
                :error-messages=forwardToErrors
                :key="state.mail + '-forwards-' + i"
              ></v-text-field>
            </v-card-text>
            <v-card-actions>
              <v-btn flat color="primary" @click="state.forwards.push('')">Flere videresendinger</v-btn>
            </v-card-actions>
          </v-card>

          <v-card class="pa-3 mt-3" raised v-if="type === 'mailbox'">
            <h3 v-if="isNew">Passord</h3>
            <h3 v-else>Evt. nytt passord</h3>

            <password-input
              :error-messages=emailErrors
              :confirm="true"
              :numCharacters="8"
              @passwordChanged="passwordChanged"
            />
          </v-card>

          <v-card class="pa-3 mt-3" raised v-if="type === 'mailbox'">
            <h3>Send oppsett</h3>
            <v-card-text>
              <p>
                Hvis du vil ha en e-post/SMS med instruksjoner og oppsett for <em v-text="state.mail"></em>,
                kan du fylle inn epost og/eller telefonnummer under, så sender vi informasjonen dit, inkl. brukernavn og passord.
              </p>
              <p>
                Hvis du ikke vil dette, kan du bare trykke "Lagre endringer" under uten å fylle inn noe.
              </p>
              <v-text-field
                label="E-postadresse"
                v-model="sendConfig.email"
              ></v-text-field>
              <v-text-field
                label="Mobilnummer"
                v-model="sendConfig.phone"
              ></v-text-field>
            </v-card-text>
          </v-card>
        </v-form>

        <v-btn
          v-if="isNew"
          color="green darken-2"
          dark
          @click="saveChanges"
        >Opprett</v-btn>
        <v-btn
          v-else
          color="green darken-2"
          dark
          @click="saveChanges"
        >Lagre</v-btn>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import DomainService from '@/services/DomainService'
import EmailService from '@/services/EmailService'
import _ from 'lodash'

export default {
  mounted () {
    if (this.isNew) {
      this.state = {
        mail: '',
        aliases: [''],
        forwards: ['']
      }
    } else {
      this.init()
    }

    this.doAnyway()
    this.$refs.focusField.focus()
  },
  data () {
    return {
      emailErrors: [],
      forwardToErrors: [],
      isSubmitting: false,
      originalEmail: '',
      password: '',
      passwordHasError: '',
      state: {},
      sendConfig: {
        dialog: false,
        email: '',
        phone: ''
      },
      domains: [],
      emailSuggestions: [],
      dialog: false
    }
  },
  methods: {
    async init () {
      const method = this.type === 'mailbox' ? 'getMailbox' : 'getForward'
      const response = await this.runService(this, 'email-detail', EmailService[method], this.mail)
      if (response) {
        this.state = response.data
        this.originalEmail = this.state.mail
      }
    },
    async doAnyway () {
      const response = await this.runService(this, 'email-detail', DomainService.getCompleteList)
      if (response) {
        this.domains = response.data
      }
    },
    async saveChanges () {
      if (this.checkForErrors()) {
        return
      }

      if (this.isNew) {
        this.createMailbox()
      } else {
        this.saveMailbox()
      }
    },
    async createMailbox () {
      const method = this.type === 'mailbox' ? 'createMailbox' : 'createForward'
      const response = await this.runService(this, 'email-detail', EmailService[method], this.state, this.password, this.sendConfig)
      if (response) {
        const term = this.type === 'mailbox' ? 'E-postkonto' : 'Videresending'
        this.goBack(term + ' ble opprettet!')
      }
    },
    async saveMailbox () {
      const method = this.type === 'mailbox' ? 'updateMailbox' : 'updateForward'
      const response = await this.runService(this, 'email-detail', EmailService[method], this.mail, this.state, this.password, this.sendConfig)
      if (response) {
        const term = this.type === 'mailbox' ? 'E-postkontoen' : 'Videresendingen'
        this.goBack(term + ' ble lagret!')
      }
    },
    async deleteMailbox () {
      this.dialog = false;
      const method = this.type === 'mailbox' ? 'deleteMailbox' : 'deleteForward'
      const response = await this.runService(this, 'email-detail', EmailService[method], this.mail)
      if (response) {
        const term = this.type === 'mailbox' ? 'E-postkontoen' : 'Videresendingen'
        this.goBack(term + ' ble slettet!')
      }
    },
    goBack (successMessage) {
      if (successMessage) {
        this.addNotification(false, {
          group: 'email-list',
          type: 'success',
          text: successMessage
        })
      }
      const hash = this.type === 'mailbox' ? undefined : '#tab-forwards'
      this.$router.push({ name: 'email-list', hash: hash })
    },
    checkForErrors () {
      this.emailErrors = []
      this.forwardToErrors = []

      let hasError = false
      if (this.state.mail.length === 0) {
        this.emailErrors = ['Du må skrive inn en e-postadresse']
        hasError = true
      }

      if (this.type === 'forward' && _.compact(this.state.forwards).length === 0) {
        this.forwardToErrors = ['Du må registrere hvor e-posten skal videresendes']
        hasError = true
      }

      if (this.type === 'mailbox') {
        if (this.isNew && !this.password) {
          this.emailErrors.push('Du må skrive inn et passord')
          hasError = true
        }

        if (this.passwordHasError) {
          hasError = true
        }
      }

      return hasError
    },
    passwordChanged (v, hasError) {
      this.password = v
      this.passwordHasError = hasError
    },
    emailSuggestion (event) {
      this.emailSuggestions = []
      var value = event.target.value

      if (value.indexOf('@') > -1) {
        value = value.split('@')[0]

        for (var i = 0; i < this.domains.length; ++i) {
          this.emailSuggestions.push(value + '@' + this.domains[i])
        }
      }
    }
  },
  computed: {
    type () {
      return this.$route.params.type
    },
    mail () {
      return this.$route.params.mail
    },
    isNew () {
      return this.mail === 'new'
    }
  }
}
</script>
